// Account audit feed data layer (#854). Reads `GET /v1/audit/events` — the
// account's recent maintainer-visible audit rows across ALL sessions, newest-
// first, keyset-paginated. The Home "Live audit" panel's View-all archive;
// per-session detail stays the SSE timeline (`lib/sessionStream`). Mirrors
// lib/digests: one `createQuery` over `apiFetch` reusing the session bearer.

import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import type { Page } from './paged';
import { SESSION_TOKEN } from './session';

/** `/v1/audit/events` row projection — see `apps/api/src/routes/audit-events.ts`. */
export type AuditFeedEvent = {
  readonly id: string;
  readonly session_id: string;
  readonly repo_full_name: string | null;
  readonly kind: string;
  readonly tool: string | null;
  /**
   * #1222: set only on `integration.used` rows, which carry `tool: null` by
   * domain invariant. Null on every other kind — the route projects these two
   * scalars for that one kind and nothing else.
   */
  readonly verb?: string | null;
  readonly outcome?: string | null;
  readonly created_at_iso: string;
};

/** One page of the feed — the canonical `/v1` slice envelope (`lib/paged`). */
export type AuditFeedPage = Page<AuditFeedEvent>;

export const AUDIT_FEED_QUERY_KEY = 'audit-feed';
const PAGE_SIZE = 50;

/**
 * One page of the account audit feed at `cursor()` (null → first page). Idle
 * (no fetch) when there's no session. The route accumulates pages on
 * "load more" — same shape as the repo Audit tab.
 *
 * `per_page` is the page-size parameter. The `?limit` alias is gone — the route
 * 400s it by name — so `per_page` is the only spelling that pages.
 */
export function useAuditFeed(cursor: () => string | null, token: string | null = SESSION_TOKEN) {
  return createQuery<AuditFeedPage>(() => ({
    queryKey: [AUDIT_FEED_QUERY_KEY, cursor(), token, activeAccountId()],
    enabled: token !== null,
    queryFn: () => {
      const params = new URLSearchParams({ per_page: String(PAGE_SIZE) });
      const c = cursor();
      if (c) params.set('cursor', c);
      return apiFetch<AuditFeedPage>(`/v1/audit/events?${params.toString()}`, token);
    },
  }));
}

export const INTEGRATION_USAGE_QUERY_KEY = 'integration-usage';
const INTEGRATION_USAGE_PAGE_SIZE = 20;

/**
 * #1222: connections-view usage feed — `GET /v1/audit/events?kind=integration.used`.
 * Tenant-fenced on the route (accountId from the session principal, then in-SQL
 * `WHERE account_id`); the `kind` filter is additive. Reuses the same row shape
 * as `useAuditFeed` so the existing `<AuditFeedList>` renders without changes.
 */
export function useIntegrationUsage(
  cursor: () => string | null,
  token: string | null = SESSION_TOKEN,
) {
  return createQuery<AuditFeedPage>(() => ({
    queryKey: [INTEGRATION_USAGE_QUERY_KEY, cursor(), token, activeAccountId()],
    enabled: token !== null,
    queryFn: () => {
      const params = new URLSearchParams({
        per_page: String(INTEGRATION_USAGE_PAGE_SIZE),
        kind: 'integration.used',
      });
      const c = cursor();
      if (c) params.set('cursor', c);
      return apiFetch<AuditFeedPage>(`/v1/audit/events?${params.toString()}`, token);
    },
  }));
}
